import { useCallback, useMemo, useState } from "react"

import { DeviceReadingType } from "src/data/devices/types/deviceTypes"
import { slugify } from "src/utils/genericUtil"
import { localStorageFactory } from "src/utils/storageUtil"

export type TParadiseGraphViewColumn = {
  id: string
  readingType: DeviceReadingType
}

export type TParadiseGraphViewRow = {
  id: string
  columns: TParadiseGraphViewColumn[]
}

export type TParadiseStoredGraphView = {
  id: string
  name: string
  graphs: TParadiseGraphViewRow[]
}

export type TParadiseGraphView = {
  id: string
  name: string
  graphs: (TParadiseGraphViewRow & {
    isFull: boolean
  })[]
  addRow: (rowIndex: number, initialReadingType: DeviceReadingType) => void
  addColumn: (
    rowIndex: number,
    columnIndex: number,
    readingType: DeviceReadingType
  ) => void
  removeColumn: (rowIndex: number, columnIndex: number) => void
  changeGraphReadingType: (args: {
    rowIndex: number
    columnIndex: number
    readingType: DeviceReadingType
  }) => void
}

const MAX_COLUMNS = 3

const graphViewsStorage = localStorageFactory<TParadiseStoredGraphView[]>({
  key: "minut.paradise.device_readings.graph_views",
})

export function useParadiseGraphViews(): {
  graphViews: TParadiseGraphView[]
  addGraphView: (name: string) => TParadiseStoredGraphView[]
  removeGraphView: (graphViewId: string) => void
  renameGraphView: (graphViewId: string, name: string) => void
} {
  const [graphViews, setGraphViews] = useState(initilizeGraphViews)

  const getGraphView = useCallback(
    (graphViewId: string) => {
      return graphViews.find((view) => view.id === graphViewId)
    },
    [graphViews]
  )

  function addGraphView(name: string) {
    const newGraphViews = [
      ...graphViews,
      // The tabs slugify the IDs, so we need to do the same here
      // Ideally we wouldn't have, but it will not affect the functionality
      { id: slugify(crypto.randomUUID()), name, graphs: [] },
    ]

    setGraphViews(newGraphViews)
    graphViewsStorage.set(newGraphViews)

    return newGraphViews
  }

  function removeGraphView(graphViewId: string) {
    const newGraphViews = graphViews.filter((view) => view.id !== graphViewId)

    setGraphViews(newGraphViews)
    graphViewsStorage.set(newGraphViews)
  }

  function renameGraphView(graphViewId: string, name: string) {
    const newGraphViews = graphViews.map((view) =>
      view.id === graphViewId ? { ...view, name } : view
    )

    setGraphViews(newGraphViews)
    graphViewsStorage.set(newGraphViews)
  }

  const addGraphViewRow = useCallback(
    (
      graphViewId: string,
      rowIndex: number,
      initialReadingType: DeviceReadingType
    ) => {
      const graphView = getGraphView(graphViewId)

      if (!graphView) {
        return
      }

      const newGraphViewRow: TParadiseGraphViewRow = {
        id: slugify(crypto.randomUUID()),
        columns: [
          {
            id: slugify(crypto.randomUUID()),
            readingType: initialReadingType,
          },
        ],
      }

      const newGraphView = {
        ...graphView,
        graphs: graphView.graphs.toSpliced(rowIndex, 0, newGraphViewRow),
      }

      const newGraphViews = graphViews.map((view) =>
        view.id === graphViewId ? newGraphView : view
      )

      setGraphViews(newGraphViews)
      graphViewsStorage.set(newGraphViews)
    },
    [graphViews, getGraphView]
  )

  const addGraphViewColumn = useCallback(
    (
      graphViewId: string,
      rowIndex: number,
      columnIndex: number,
      readingType: DeviceReadingType
    ) => {
      const graphView = getGraphView(graphViewId)

      if (!graphView) {
        return
      }

      const graphViewRow = graphView.graphs[rowIndex]

      if (!graphViewRow) {
        addGraphViewRow(graphViewId, rowIndex, readingType)
        return
      }

      if (graphViewRow.columns.length >= MAX_COLUMNS) {
        return
      }

      const newGraphView = {
        ...graphView,
        graphs: graphView.graphs.map((row, i) =>
          i === rowIndex
            ? {
                ...row,
                columns: row.columns.toSpliced(columnIndex, 0, {
                  id: slugify(crypto.randomUUID()),
                  readingType,
                }),
              }
            : row
        ),
      }

      const newGraphViews = graphViews.map((view) =>
        view.id === graphViewId ? newGraphView : view
      )

      setGraphViews(newGraphViews)
      graphViewsStorage.set(newGraphViews)
    },
    [graphViews, getGraphView, addGraphViewRow]
  )

  const removeGraphViewColumn = useCallback(
    (graphViewId: string, rowIndex: number, columnIndex: number) => {
      const graphView = getGraphView(graphViewId)

      if (!graphView) {
        return
      }

      const newGraphView = {
        ...graphView,
        graphs: graphView.graphs
          .map((row, i) =>
            i === rowIndex
              ? {
                  ...row,
                  columns: row.columns.filter((_, j) => j !== columnIndex),
                }
              : row
          )
          // Make sure to remove empty rows
          .filter((row) => row.columns.length > 0),
      }

      const newGraphViews = graphViews.map((view) =>
        view.id === graphViewId ? newGraphView : view
      )

      setGraphViews(newGraphViews)
      graphViewsStorage.set(newGraphViews)
    },
    [graphViews, getGraphView]
  )

  const changeGraphReadingType = useCallback(
    (
      graphViewId: string,
      {
        rowIndex,
        columnIndex,
        readingType,
      }: {
        rowIndex: number
        columnIndex: number
        readingType: DeviceReadingType
      }
    ) => {
      const graphView = getGraphView(graphViewId)

      if (!graphView) {
        return
      }

      const newGraphView = {
        ...graphView,
        graphs: graphView.graphs.map((row, i) =>
          i === rowIndex
            ? {
                ...row,
                columns: row.columns.map((column, j) =>
                  j === columnIndex ? { ...column, readingType } : column
                ),
              }
            : row
        ),
      }

      const newGraphViews = graphViews.map((view) =>
        view.id === graphViewId ? newGraphView : view
      )

      setGraphViews(newGraphViews)
      graphViewsStorage.set(newGraphViews)
    },
    [graphViews, getGraphView]
  )

  const graphViewsWithMethods: TParadiseGraphView[] = useMemo(() => {
    return graphViews.map((view) => ({
      ...view,
      graphs: view.graphs.map((row) => ({
        ...row,
        isFull: row.columns.length >= MAX_COLUMNS,
      })),
      addRow: addGraphViewRow.bind(null, view.id),
      addColumn: addGraphViewColumn.bind(null, view.id),
      removeColumn: removeGraphViewColumn.bind(null, view.id),
      changeGraphReadingType: changeGraphReadingType.bind(null, view.id),
    }))
  }, [
    graphViews,
    changeGraphReadingType,
    addGraphViewRow,
    addGraphViewColumn,
    removeGraphViewColumn,
  ])

  return {
    graphViews: graphViewsWithMethods,
    addGraphView,
    removeGraphView,
    renameGraphView,
  }
}

function initilizeGraphViews(): TParadiseStoredGraphView[] {
  return graphViewsStorage.get() ?? []
}
